import uniqueId from 'lodash-es/uniqueId'

import { EMPTY_LIST, EMPTY_STRING } from '../../../constants/common'
import {
  COMPANY_CONTACT_TYPE,
  PROFILE_CONTACT_TYPE,
} from '../../../constants/contact'
import { SERVER_ERROR_OBJECT_KEY } from '../../../constants/validation'

import {
  COMPANY_CONTACT_TYPE_CONFIG,
  PROFILE_CONTACT_TYPE_CONFIG,
  PROFILE_CONTACTS_WIDGET_SERVER_ERROR_NOTIFICATION_KEY,
  PROVIDER_CONTACT_TYPE_CONFIG,
} from './ProfileContactsWidget.constants'
import { PROFILE_CONTACTS_WIDGET_VALUE_FIELD } from './ProfileContactsWidgetListCell'

function getContactType(contact) {
  return contact.typeConfig.type
}

function getSavedContactsCount(contacts) {
  return contacts.filter(contact => !isNoValueContact(contact)).length
}

export function getProfileContactsWidgetErrorNotificationKey(error) {
  return (
    PROFILE_CONTACTS_WIDGET_SERVER_ERROR_NOTIFICATION_KEY[
      error?.response?.data?.[SERVER_ERROR_OBJECT_KEY]
    ] ?? 'Error.CommonApiRequestFailKey'
  )
}

export function getTypeConfig({ configMap, fallbackType, type }) {
  return { ...(configMap[type] ?? configMap[fallbackType]), type }
}

export function getCompanyTypeConfig(type) {
  return getTypeConfig({
    configMap: COMPANY_CONTACT_TYPE_CONFIG,
    fallbackType: COMPANY_CONTACT_TYPE.WEBSITE,
    type,
  })
}

export function getProfileTypeConfig(
  type,
  configMap = PROFILE_CONTACT_TYPE_CONFIG
) {
  return getTypeConfig({
    configMap,
    fallbackType: PROFILE_CONTACT_TYPE.WEBSITE,
    type,
  })
}

export function getProviderTypeConfig(type) {
  return getTypeConfig({
    configMap: PROVIDER_CONTACT_TYPE_CONFIG,
    fallbackType: PROFILE_CONTACT_TYPE.WEBSITE,
    type,
  })
}

// A placeholder row for a type whose contact is absent but must stay visible
// (e.g. the provider "Main email" or the profile auth email/phone). It is not
// a real contact: saving an editable one creates the contact via api.create.
export function createNoValueContact({ canEdit = false, typeConfig }) {
  return {
    canDelete: false,
    canEdit,
    id: `noValue_${typeConfig.type}`,
    isNoValue: true,
    typeConfig,
    value: null,
  }
}

export function isNoValueContact(contact) {
  return Boolean(contact.isNoValue)
}

export function getContactValue({ typeConfig, value }) {
  if (!typeConfig.isPhone) {
    return value
  }

  return value.startsWith('+') ? value : `+${value}`
}

export function getContactQaName({ id, qaName }) {
  return `${qaName}_${id}`
}

export function sortByOrder({ getConfig, items }) {
  return [...items].sort((a, b) => getConfig(a).order - getConfig(b).order)
}

export function addNewContact({ newContacts, typeConfig }) {
  return [...newContacts, createNewContact(typeConfig)]
}

export function createNewContact(typeConfig) {
  return { id: uniqueId(), typeConfig }
}

export function getInitialValues() {
  return { value: EMPTY_STRING }
}

export function getIsEmpty({ contacts, newContacts }) {
  return contacts.length === 0 && newContacts.length === 0
}

export function getExistingTypes({ contacts, newContacts }) {
  return [
    ...(contacts ?? EMPTY_LIST)
      .filter(contact => !isNoValueContact(contact))
      .map(getContactType),
    ...(newContacts ?? EMPTY_LIST).map(getContactType),
  ]
}

export function getIsLimitReached({ contacts, limit, newContacts }) {
  return (
    limit !== null &&
    getSavedContactsCount(contacts) + newContacts.length >= limit
  )
}

export function getNewContactsInitialValues(newContacts) {
  return newContacts.reduce((acc, contact) => {
    acc[contact.id] = getInitialValues()

    return acc
  }, {})
}

export function getAreNewContactsFilled({ newContacts, values }) {
  return newContacts.every(contact =>
    Boolean(values[contact.id]?.[PROFILE_CONTACTS_WIDGET_VALUE_FIELD])
  )
}

export function removeContactById({ contactId, newContacts }) {
  return newContacts.filter(contact => contact.id !== contactId)
}

export function removeContactsByIds({ contactIds, newContacts }) {
  return newContacts.filter(contact => !contactIds.includes(contact.id))
}
